import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import type { IEstoqueItemRow } from "interfaces/loja/estoque";
import MDButton from "components/MDButton";

type Props = {
  open: boolean;
  onClose: () => void;
  row: IEstoqueItemRow | null;
  onSubmit: (payload: {
    variacaoId?: string;
    produtoId?: string;
    delta: number;
    observacao: string;
  }) => Promise<boolean>;
};

export default function AjusteEstoqueDialog({
  open,
  onClose,
  row,
  onSubmit,
}: Props) {
  const [delta, setDelta] = useState<string>("");
  const [observacao, setObservacao] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && row) {
      setDelta("");
      setObservacao("");
    }
  }, [open, row]);

  const handleSubmit = async () => {
    if (!row) return;
    const d = parseInt(delta, 10);
    if (Number.isNaN(d) || d === 0) return;
    const obs = observacao.trim();
    if (!obs) return;
    setLoading(true);
    try {
      const payload =
        row.tipo === "VARIACAO" && row.variacaoId
          ? { variacaoId: row.variacaoId, delta: d, observacao: obs }
          : { produtoId: row.produtoId, delta: d, observacao: obs };
      const ok = await onSubmit(payload);
      if (ok) onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ajuste manual de estoque</DialogTitle>
      <DialogContent>
        {row ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert
              severity={row.estoque === 0 ? "error" : "warning"}
              variant="outlined"
              sx={{
                borderRadius: 2,
                alignItems: "flex-start",
                "& .MuiAlert-icon": { color: "warning.main" },
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>{row.produtoNome}</strong>
                {row.rotuloVariacao ? ` · ${row.rotuloVariacao}` : ""}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                SKU: {row.sku} · Saldo atual: <strong>{row.estoque}</strong> un.
              </Typography>
            </Alert>

            <TextField
              label="Variação no estoque (delta)"
              type="number"
              fullWidth
              required
              helperText="Use valores positivos para entrada e negativos para saída."
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
            />
            <TextField
              label="Motivo / observação"
              fullWidth
              required
              multiline
              minRows={2}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <MDButton
          variant="contained"
          color="success"
          disabled={
            loading ||
            !row ||
            !observacao.trim() ||
            !delta ||
            parseInt(delta, 10) === 0 ||
            Number.isNaN(parseInt(delta, 10))
          }
          onClick={() => void handleSubmit()}
        >
          {loading ? "Salvando…" : "Confirmar ajuste"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
